import React, { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Col, Row, Modal, ModalHeader, ModalBody, ModalFooter,
  Card, CardBody, Nav, NavItem, NavLink, Badge, Button, Spinner, Table
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const durumRenk = (durum) => {
  const map = {
    "Beklemede": "warning",
    "Hazırlanıyor": "info",
    "Kargoya Verildi": "primary",
    "Teslim Edildi": "success",
    "İptal Edildi": "danger",
  };
  return map[durum] || "secondary";
};

const Faturalarim = () => {
  document.title = "Siparişlerim | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const userId = authUser?.ID;
  const isAdmin = authUser?.HESAP_TURU === "Admin";

  const [siparisler, setSiparisler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("tumu");

  const [detailModal, setDetailModal] = useState(false);
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [detaylar, setDetaylar] = useState([]);
  const [isLoadingDetay, setIsLoadingDetay] = useState(false);

  const fetchSiparisler = async () => {
    if (!userId) return;
    setIsLoading(true);
    try {
      const filterStr = isAdmin ? "" : `$filter=KULLANICI_ID eq ${userId}&`;
      const resp = await fetch(
        `${BASE_URL}/SIPARIS?${filterStr}$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO)&$orderby=ID desc&$top=500`
      );
      const result = await resp.json();
      if (result?.value) setSiparisler(result.value);
    } catch {
      toast.error("Siparişler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSiparisler();
  }, [userId]);

  const handleViewDetail = async (siparis) => {
    setSelectedSiparis(siparis);
    setDetailModal(true);
    setIsLoadingDetay(true);
    setDetaylar([]);
    try {
      const resp = await fetch(
        `${BASE_URL}/SIPARIS_DETAY?$filter=SIPARIS_ID eq ${siparis.ID}&$expand=ILANLAR($select=AD,FIYAT,KATEGORILER_IMAGE)&$top=100`
      );
      const result = await resp.json();
      if (result?.value) setDetaylar(result.value);
    } catch {
      toast.error("Sipariş detayları yüklenemedi.");
    } finally {
      setIsLoadingDetay(false);
    }
  };

  const tabs = [
    { id: "tumu", label: "Tümü" },
    { id: "Beklemede", label: "Beklemede" },
    { id: "Hazırlanıyor", label: "Hazırlanıyor" },
    { id: "Kargoya Verildi", label: "Kargoda" },
    { id: "Teslim Edildi", label: "Teslim Edildi" },
    { id: "İptal Edildi", label: "İptal Edildi" },
  ];

  const filteredData = useMemo(() => {
    if (activeTab === "tumu") return siparisler;
    return siparisler.filter(s => s.SIPARIS_DURUMU === activeTab);
  }, [siparisler, activeTab]);

  const tabCount = (id) => id === "tumu"
    ? siparisler.length
    : siparisler.filter(s => s.SIPARIS_DURUMU === id).length;

  const columns = useMemo(() => [
    {
      header: "Sipariş No",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => <span className="fw-bold text-primary">#{c.getValue()}</span>
    },
    {
      header: "Müşteri",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: c => {
        const u = c.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">-</span>;
      }
    },
    {
      header: "Toplam Tutar",
      accessorKey: "TOPLAM_TUTAR",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => (
        <span className="fw-bold text-success">
          {parseFloat(c.getValue() || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
        </span>
      )
    },
    {
      header: "Durum",
      accessorKey: "SIPARIS_DURUMU",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => (
        <Badge color={durumRenk(c.getValue())}>
          {c.getValue() || "Bilinmiyor"}
        </Badge>
      )
    },
    {
      header: "Tarih",
      accessorKey: "SIPARIS_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: c => c.getValue()
        ? new Date(c.getValue()).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "-"
    },
    {
      header: "İşlem",
      enableSorting: false,
      enableColumnFilter: false,
      cell: cellProps => (
        <Button
          color="primary"
          size="sm"
          outline
          onClick={() => handleViewDetail(cellProps.row.original)}
        >
          <i className="bx bx-search me-1" />Detay
        </Button>
      )
    }
  ], [siparisler]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Hesabım" breadcrumbItem="Siparişlerim" />

          {/* İstatistik kartları */}
          <Row className="mb-3">
            {[
              { label: "Toplam Sipariş", value: siparisler.length, color: "primary", icon: "bx-cart" },
              { label: "Beklemede", value: siparisler.filter(s => s.SIPARIS_DURUMU === "Beklemede").length, color: "warning", icon: "bx-time" },
              { label: "Teslim Edildi", value: siparisler.filter(s => s.SIPARIS_DURUMU === "Teslim Edildi").length, color: "success", icon: "bx-check-circle" },
              { label: "İptal Edildi", value: siparisler.filter(s => s.SIPARIS_DURUMU === "İptal Edildi").length, color: "danger", icon: "bx-x-circle" },
            ].map((s, i) => (
              <Col xs={6} md={3} key={i}>
                <Card className="mini-stats-wid shadow-sm border-0 mb-3">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-1 font-size-13">{s.label}</p>
                        <h4 className="mb-0">{isLoading ? <Spinner size="sm" color={s.color} /> : s.value}</h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle bg-${s.color}`}>
                          <i className={`bx ${s.icon} font-size-20`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="shadow-sm">
            <CardBody className="pt-0 pb-0 border-bottom">
              <Nav tabs className="nav-tabs-custom border-bottom-0">
                {tabs.map(t => (
                  <NavItem key={t.id}>
                    <NavLink
                      className={classnames({ active: activeTab === t.id })}
                      onClick={() => setActiveTab(t.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {t.label}
                      {tabCount(t.id) > 0 && (
                        <Badge color={activeTab === t.id ? "primary" : "light"} className={`ms-1 ${activeTab !== t.id ? "text-dark" : ""}`}>
                          {tabCount(t.id)}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </CardBody>

            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{tabs.find(t => t.id === activeTab)?.label} Siparişler</h5>
                <Button color="primary" size="sm" outline onClick={fetchSiparisler} disabled={isLoading}>
                  <i className="bx bx-refresh me-1" />Yenile
                </Button>
              </div>

              {isLoading ? (
                <div className="text-center py-5"><Spinner color="primary" /></div>
              ) : (
                <TableContainer
                  columns={columns}
                  data={filteredData}
                  isGlobalFilter={true}
                  SearchPlaceholder="Sipariş veya müşteri ara..."
                  isPagination={true}
                  isCustomPageSize={true}
                  tableClass="table-bordered align-middle nowrap w-100 mb-0"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                />
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Sipariş Detay Modal */}
      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg" centered scrollable>
        <ModalHeader toggle={() => setDetailModal(false)} className="bg-light">
          Sipariş Detayı{selectedSiparis && ` — #${selectedSiparis.ID}`}
        </ModalHeader>
        <ModalBody>
          {selectedSiparis && (
            <>
              {/* Müşteri Bilgisi */}
              <div className="mb-4">
                <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">Müşteri</h6>
                <Card className="shadow-none border bg-light mb-0">
                  <CardBody className="p-3">
                    <Row>
                      <Col sm={4}>
                        <small className="text-muted d-block">Ad Soyad</small>
                        <span className="fw-medium">{selectedSiparis.KULLANICILAR?.AD_SOYAD || "-"}</span>
                      </Col>
                      <Col sm={4}>
                        <small className="text-muted d-block">E-posta</small>
                        <span className="fw-medium">{selectedSiparis.KULLANICILAR?.Mail_Adress || "-"}</span>
                      </Col>
                      <Col sm={4}>
                        <small className="text-muted d-block">Telefon</small>
                        <span className="fw-medium">{selectedSiparis.KULLANICILAR?.CEP_NO || "-"}</span>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </div>

              {/* Sipariş Bilgisi */}
              <div className="mb-4">
                <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">Sipariş Bilgisi</h6>
                <Row>
                  <Col sm={4}>
                    <small className="text-muted d-block">Sipariş Tarihi</small>
                    <span className="fw-medium">
                      {selectedSiparis.SIPARIS_TARIHI
                        ? new Date(selectedSiparis.SIPARIS_TARIHI).toLocaleDateString("tr-TR")
                        : "-"}
                    </span>
                  </Col>
                  <Col sm={4}>
                    <small className="text-muted d-block">Toplam Tutar</small>
                    <span className="fw-bold text-success fs-6">
                      {parseFloat(selectedSiparis.TOPLAM_TUTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                    </span>
                  </Col>
                  <Col sm={4}>
                    <small className="text-muted d-block">Durum</small>
                    <Badge color={durumRenk(selectedSiparis.SIPARIS_DURUMU)}>
                      {selectedSiparis.SIPARIS_DURUMU || "-"}
                    </Badge>
                  </Col>
                </Row>
              </div>

              {/* Sipariş Kalemleri */}
              <div>
                <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">
                  Sipariş Kalemleri ({detaylar.length})
                </h6>
                {isLoadingDetay ? (
                  <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
                ) : detaylar.length > 0 ? (
                  <Table bordered size="sm" className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Ürün</th>
                        <th className="text-center">Adet</th>
                        <th className="text-end">Birim Fiyat</th>
                        <th className="text-end">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detaylar.map((d, i) => (
                        <tr key={i}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {d.ILANLAR?.KATEGORILER_IMAGE && (
                                <img
                                  src={d.ILANLAR.KATEGORILER_IMAGE}
                                  alt=""
                                  style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4 }}
                                />
                              )}
                              <span className="fw-medium">{d.ILANLAR?.AD || `Ürün #${d.ILAN_ID}`}</span>
                            </div>
                          </td>
                          <td className="text-center">{d.ADET || 1}</td>
                          <td className="text-end">
                            {parseFloat(d.BIRIM_FIYAT || d.ILANLAR?.FIYAT || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                          </td>
                          <td className="text-end fw-bold">
                            {parseFloat((d.ADET || 1) * (d.BIRIM_FIYAT || d.ILANLAR?.FIYAT || 0)).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="text-end fw-bold">Genel Toplam</td>
                        <td className="text-end fw-bold text-success">
                          {parseFloat(selectedSiparis.TOPLAM_TUTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="bx bx-package fs-2 mb-2 d-block opacity-50" />
                    Sipariş kalemi bulunamadı.
                  </div>
                )}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>

      <ToastContainer autoClose={2000} />
    </React.Fragment>
  );
};

export default Faturalarim;
